// CampaignList.jsx — same card-grid layout as JourneyList
import React, { useState, useEffect } from "react";
import { Spinner } from "react-bootstrap";
import { FaPlus, FaEdit, FaTrash, FaCopy, FaWhatsapp, FaSms, FaEnvelope } from "react-icons/fa";
import dayjs from "dayjs";
import axios from "axios";
import { apiUrl, authHeaders } from "./config";

const C = {
  brand:"#4f46e5", brandLt:"#eef2ff",
  green:"#059669", greenLt:"#ecfdf5",
  amber:"#d97706", amberLt:"#fffbeb",
  text:"#111827", muted:"#6b7280", subtle:"#9ca3af",
  border:"#e5e7eb", bg:"#f9fafb", white:"#ffffff",
  danger:"#ef4444",
};

const CHANNEL_META = {
  WhatsApp: { icon: <FaWhatsapp />, color:"#25d366", bg:"#f0fdf4" },
  SMS:      { icon: <FaSms />,      color:"#6c757d", bg:"#f9fafb" },
  Email:    { icon: <FaEnvelope />, color:"#0d6efd", bg:"#eff6ff" },
};

const TYPE_META = {
  "one-time":    { emoji:"📣", color:"#4f46e5", bg:"#eef2ff" },
  birthday:      { emoji:"🎂", color:"#7c3aed", bg:"#f5f3ff" },
  anniversary:   { emoji:"💍", color:"#be185d", bg:"#fff1f2" },
  checkin:       { emoji:"🏨", color:"#0369a1", bg:"#eff6ff" },
  checkout:      { emoji:"👋", color:"#0f766e", bg:"#f0fdfa" },
  "profile_completion": { emoji:"👤", color:"#d97706", bg:"#fffbeb" },
};

const statusColors = {
  Scheduled: { bg:"#fef9c3", color:"#92400e" },
  Active:    { bg:"#dcfce7", color:"#15803d" },
  Completed: { bg:"#dbeafe", color:"#1d4ed8" },
  Draft:     { bg:"#f3f4f6", color:"#6b7280" },
  Paused:    { bg:"#f3f4f6", color:"#6b7280" },
  Cancelled: { bg:"#fee2e2", color:"#991b1b" },
};

const StatusPill = ({ status }) => {
  const s = statusColors[status] || statusColors.Draft;
  return (
    <span style={{ fontSize:"0.68rem", fontWeight:700, padding:"3px 10px",
      borderRadius:20, background:s.bg, color:s.color, whiteSpace:"nowrap" }}>
      {status}
    </span>
  );
};

const actionBtn = (color, bg) => ({
  flex:1, height:30, borderRadius:7, border:"none",
  background:bg, color, cursor:"pointer",
  display:"flex", alignItems:"center", justifyContent:"center",
});

export const ChannelIcon = ({ channel }) => {
  const m = CHANNEL_META[channel] || CHANNEL_META.WhatsApp;
  return <span style={{ color:m.color }}>{m.icon}</span>;
};

const CampaignList = ({ onNavigate }) => {
  const [campaigns,   setCampaigns]   = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [confirmId,   setConfirmId]   = useState(null);
  const [confirmName, setConfirmName] = useState("");

  useEffect(() => { fetchCampaigns(); }, []);

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/feedback/campaigns`, authHeaders());
      setCampaigns((res.data || []).map(c => ({
        ...c,
        id:          c.campaignid   || c.id,
        channel:     c.fallback?.channels?.[0] || "WhatsApp",
        audience:    c.audience_count,
        conversions: c.converted,
        revenue:     c.revenue_actual,
        createdAt:   c.created_at,
      })));
    } catch { setCampaigns([]); }
    finally  { setLoading(false); }
  };

  const duplicate = async (c) => {
    const payload = {
      name: `${c.name} (Copy)`, eventType: c.campaign_type||c.eventType,
      eventDate: c.event_date||c.eventDate, filters: c.filters||{},
      steps: c.steps||[], fallback: c.fallback||{},
      templateId: c.template_id||c.templateId||"",
      landingPageId: c.landing_page_id||c.landingPageId||"",
      revenueGoal: c.revenue_goal||0, audienceCount: 0,
      totalCredits: 0, status: "Draft",
    };
    try {
      const res = await axios.post(`${apiUrl}/feedback/campaigns`, payload, authHeaders());
      setCampaigns(p => [{ ...res.data, id:res.data.campaignid }, ...p]);
    } catch {
      setCampaigns(p => [{ ...payload, id:`c${Date.now()}` }, ...p]);
    }
  };

  const deleteCampaign = async () => {
    try { await axios.delete(`${apiUrl}/feedback/campaigns/${confirmId}`, authHeaders()); } catch {}
    setCampaigns(p => p.filter(c => (c.campaignid||c.id) !== confirmId));
    setConfirmId(null);
  };

  if (loading) return (
    <div style={{ display:"flex", justifyContent:"center", alignItems:"center", minHeight:300 }}>
      <Spinner animation="border" style={{ color:C.brand }} />
    </div>
  );

  return (
    <div style={{ padding:"28px 32px" }}>

      {/* Header */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:28 }}>
        <div>
          <h2 style={{ fontSize:"1.45rem", fontWeight:800, color:C.text,
            letterSpacing:"-0.03em", margin:0 }}>
            Campaigns
          </h2>
          <p style={{ fontSize:"0.84rem", color:C.muted, margin:"4px 0 0" }}>
            One-time guest broadcasts — promotions, offers, announcements
          </p>
        </div>
        <button onClick={() => onNavigate("campaigns-create")}
          style={{ display:"inline-flex", alignItems:"center", gap:8,
            padding:"10px 20px", borderRadius:10, border:"none",
            background:C.brand, color:C.white, fontSize:"0.84rem",
            fontWeight:700, cursor:"pointer",
            boxShadow:"0 4px 14px rgba(79,70,229,0.3)" }}>
          <FaPlus size={11} /> New Campaign
        </button>
      </div>

      {/* Empty state */}
      {campaigns.length === 0 && (
        <div style={{ textAlign:"center", padding:"72px 32px",
          background:C.white, borderRadius:16, border:`1px dashed ${C.border}` }}>
          <div style={{ width:64, height:64, borderRadius:16, background:C.brandLt,
            display:"flex", alignItems:"center", justifyContent:"center",
            margin:"0 auto 16px", fontSize:"1.8rem" }}>
            📣
          </div>
          <div style={{ fontSize:"1rem", fontWeight:700, color:C.text, marginBottom:8 }}>
            No campaigns yet
          </div>
          <p style={{ fontSize:"0.84rem", color:C.muted, marginBottom:20,
            maxWidth:360, margin:"0 auto 20px" }}>
            Create a campaign to send promotions, birthday offers, anniversary packages
            and announcements directly to your guests.
          </p>
          <button onClick={() => onNavigate("campaigns-create")}
            style={{ padding:"10px 24px", borderRadius:10, border:"none",
              background:C.brand, color:C.white, fontSize:"0.84rem",
              fontWeight:700, cursor:"pointer" }}>
            <FaPlus size={11} style={{ marginRight:6 }} /> Create First Campaign
          </button>
        </div>
      )}

      {/* Campaign card grid — 3 columns */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14 }}>
        {campaigns.map(c => {
          const id      = c.campaignid || c.id;
          const typeMeta = TYPE_META[c.campaign_type||c.eventType||"one-time"] || TYPE_META["one-time"];
          const chMeta   = CHANNEL_META[c.channel] || CHANNEL_META.WhatsApp;

          return (
            <div key={id} style={{
              background:C.white, borderRadius:12,
              border:`1px solid ${C.border}`,
              borderTop:`3px solid ${typeMeta.color}`,
              padding:"16px", cursor:"pointer", transition:"all 0.15s",
            }}
            onMouseEnter={e=>{ e.currentTarget.style.boxShadow="0 4px 18px rgba(0,0,0,0.09)"; e.currentTarget.style.transform="translateY(-2px)"; }}
            onMouseLeave={e=>{ e.currentTarget.style.boxShadow="none"; e.currentTarget.style.transform="none"; }}>

              {/* Top: emoji + status */}
              <div style={{ display:"flex", justifyContent:"space-between",
                alignItems:"flex-start", marginBottom:10 }}>
                <div style={{ width:38, height:38, borderRadius:10,
                  background:typeMeta.bg, display:"flex", alignItems:"center",
                  justifyContent:"center", fontSize:"1.2rem" }}>
                  {typeMeta.emoji}
                </div>
                <StatusPill status={c.status} />
              </div>

              {/* Name */}
              <div style={{ fontSize:"0.88rem", fontWeight:700, color:C.text,
                marginBottom:4, lineHeight:1.3 }}>
                {c.name}
              </div>

              {/* Channel + date */}
              <div style={{ fontSize:"0.74rem", color:C.muted, marginBottom:12,
                display:"flex", alignItems:"center", gap:6 }}>
                <span style={{ padding:"1px 7px", borderRadius:20, fontSize:"0.68rem",
                  fontWeight:600, background:chMeta.bg, color:chMeta.color,
                  display:"flex", alignItems:"center", gap:4 }}>
                  {chMeta.icon} {c.channel}
                </span>
                {c.createdAt && (
                  <>
                    <span>·</span>
                    <span>{dayjs(c.createdAt).format("DD MMM YYYY")}</span>
                  </>
                )}
              </div>

              {/* Stats */}
              <div style={{ display:"flex", gap:0,
                borderTop:`1px solid ${C.border}`, paddingTop:10, marginBottom:12 }}>
                {[
                  { label:"Audience", val:(c.audience||0).toLocaleString(),    color:C.text  },
                  { label:"Sent",     val:(c.sent||0).toLocaleString(),         color:C.muted },
                  { label:"Revenue",  val:`₹${((Number(c.revenue)||0)/1000).toFixed(1)}K`, color:C.green },
                ].map(({ label, val, color }, i) => (
                  <div key={label} style={{ flex:1, textAlign:"center",
                    borderLeft: i>0 ? `1px solid ${C.border}` : "none" }}>
                    <div style={{ fontSize:"0.9rem", fontWeight:800, color }}>{val}</div>
                    <div style={{ fontSize:"0.6rem", color:C.subtle,
                      textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div style={{ display:"flex", gap:6 }}
                onClick={e => e.stopPropagation()}>
                <button title="Edit"
                  onClick={() => onNavigate("campaigns-edit", c)}
                  style={actionBtn(C.brand, "#eef2ff")}>
                  <FaEdit size={12} />
                </button>
                <button title="Duplicate"
                  onClick={() => duplicate(c)}
                  style={actionBtn("#0891b2", "#ecfeff")}>
                  <FaCopy size={12} />
                </button>
                <button title="Delete"
                  onClick={() => { setConfirmId(id); setConfirmName(c.name); }}
                  style={actionBtn(C.danger, "#fef2f2")}>
                  <FaTrash size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Delete confirm */}
      {confirmId && (
        <div style={{ position:"fixed", inset:0, zIndex:1060,
          display:"flex", alignItems:"center", justifyContent:"center",
          background:"rgba(0,0,0,0.4)" }}>
          <div style={{ background:C.white, borderRadius:16, padding:"28px 32px",
            width:380, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize:"1rem", fontWeight:800, color:C.text, marginBottom:8 }}>
              Delete Campaign?
            </div>
            <p style={{ fontSize:"0.84rem", color:C.muted, marginBottom:24 }}>
              <strong>{confirmName}</strong> will be permanently deleted.
            </p>
            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button onClick={() => setConfirmId(null)}
                style={{ padding:"8px 20px", borderRadius:8, border:`1px solid ${C.border}`,
                  background:C.white, color:C.text, fontSize:"0.84rem",
                  fontWeight:600, cursor:"pointer" }}>
                Cancel
              </button>
              <button onClick={deleteCampaign}
                style={{ padding:"8px 20px", borderRadius:8, border:"none",
                  background:C.danger, color:C.white, fontSize:"0.84rem",
                  fontWeight:700, cursor:"pointer" }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignList;
